// Hand-rolled relative-time formatter keyed to the i18n string table.
//
// We don't use `Intl.RelativeTimeFormat` because the PT-AO copy needs
// full control (the parity test pins both locales), and the buckets here
// are coarse (just-now / minutes / hours / days) — enough for a "last
// active {ago}" / "Updated {ago}" label, not a precise clock.
//
// Pure + deterministic in (`fromMs`, `nowMs`): no `Date.now()` inside,
// so tests freeze both ends. The returned fragment carries its own
// "ago"/"há" framing (e.g. "5 minutes ago" / "há 5 minutos").

import type { Language } from "./i18n";
import { tn } from "./i18n";

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const JUST_NOW_BELOW_MS = 45 * SECOND;

/** Format the gap between `fromMs` and `nowMs` as a localized "ago"
 * fragment. Negative gaps (clock skew putting `fromMs` slightly ahead)
 * clamp to "just now". */
export function formatRelative(
  fromMs: number,
  nowMs: number,
  language: Language,
): string {
  const diff = Math.max(0, nowMs - fromMs);

  if (diff < JUST_NOW_BELOW_MS) {
    return tn("time.just_now", {}, language);
  }
  if (diff < HOUR) {
    const n = Math.min(59, Math.max(1, Math.round(diff / MINUTE)));
    return tn(n === 1 ? "time.minutes.one" : "time.minutes.other", { n }, language);
  }
  if (diff < DAY) {
    const n = Math.min(23, Math.max(1, Math.round(diff / HOUR)));
    return tn(n === 1 ? "time.hours.one" : "time.hours.other", { n }, language);
  }
  const n = Math.max(1, Math.round(diff / DAY));
  return tn(n === 1 ? "time.days.one" : "time.days.other", { n }, language);
}
